const normalizedString = (value) =>
  value === undefined || value === null
    ? ""
    : String(value).trim().toLowerCase();

const RETRYABLE_BULK_STATUSES = new Set([
  "Pending",
  "Unprocessed",
  "Error-CNF",
]);

const SETTLEMENT_TERMINAL_BULK_STATUSES = new Set([
  "NRML",
  "NODR",
  "D1",
  "D2",
  "NOF",
  "UND",
  "Error in Preprocess",
]);

export const isBulkRetryableStatus = (status) =>
  RETRYABLE_BULK_STATUSES.has(status);

export const isBulkSettlementTerminalStatus = (status) =>
  SETTLEMENT_TERMINAL_BULK_STATUSES.has(status);

const canProjectBulkStatusTransition = (currentStatus, nextStatus) => {
  if (currentStatus === "Pending") return true;
  return (
    (currentStatus === "Unprocessed" || currentStatus === "Error-CNF") &&
    isBulkSettlementTerminalStatus(nextStatus)
  );
};

export const normalizeBulkLaterality = (value) => {
  const laterality = normalizedString(value);
  if (laterality === "left" || laterality === "os") return "left";
  if (laterality === "right" || laterality === "od") return "right";
  return "";
};

const oneConsistentValue = (values) => {
  const present = values.map(normalizedString).filter(Boolean);
  return new Set(present).size === 1 ? present[0] : "";
};

export const getBulkRowGuid = (row = {}) => {
  const statuses = Array.isArray(row.status) ? row.status : [];
  return oneConsistentValue([
    row.guid,
    row.info?.guid,
    ...statuses.map((status) => status?.guid),
  ]);
};

export const getBulkRowTuples = (row = {}) => {
  const statuses = Array.isArray(row.status) ? row.status : [];
  const guid = getBulkRowGuid(row);
  const laterality = oneConsistentValue([
    normalizeBulkLaterality(row.laterality),
    normalizeBulkLaterality(row.eye),
    normalizeBulkLaterality(row.side),
    normalizeBulkLaterality(row.info?.laterality),
    normalizeBulkLaterality(row.info?.eye),
    normalizeBulkLaterality(row.info?.side),
  ]);
  if (!guid || !laterality) return [];

  const sourceStatuses = statuses.length ? statuses : [row];
  const tuples = sourceStatuses.map((status) => {
    const statusGuid = normalizedString(status?.guid) || guid;
    const model = normalizedString(
      status?.model || status?.Model || row.model || row.Model
    );
    const statusLaterality =
      normalizeBulkLaterality(
        status?.laterality || status?.eye || status?.side
      ) || laterality;
    if (statusGuid !== guid || !model || statusLaterality !== laterality) {
      return "";
    }
    return `${guid}|${model}|${laterality}`;
  });

  if (tuples.some((tuple) => !tuple)) return [];
  return Array.from(new Set(tuples)).sort();
};

export const hasSameBulkRowTuples = (left, right) => {
  const leftTuples = getBulkRowTuples(left);
  const rightTuples = getBulkRowTuples(right);
  return (
    leftTuples.length > 0 &&
    leftTuples.length === rightTuples.length &&
    leftTuples.every((tuple, index) => tuple === rightTuples[index])
  );
};

export const getBulkEventTuple = (event = {}) => {
  const guid = normalizedString(event.guid);
  const model = normalizedString(event.model);
  const laterality = normalizeBulkLaterality(event.laterality);
  return guid && model && laterality
    ? `${guid}|${model}|${laterality}`
    : "";
};

export const projectBulkStatusEvent = (rows = [], event = {}) => {
  const eventTuple = getBulkEventTuple(event);
  if (!eventTuple || !normalizedString(event.status) || !Array.isArray(rows)) {
    return { outcome: "ignored", rows };
  }

  const matches = [];
  rows.forEach((row, rowIndex) => {
    const rowTuples = getBulkRowTuples(row);
    if (!rowTuples.includes(eventTuple) || !Array.isArray(row.status)) return;
    row.status.forEach((status, statusIndex) => {
      const statusTuple = getBulkRowTuples({ ...row, status: [status] });
      if (statusTuple.length === 1 && statusTuple[0] === eventTuple) {
        matches.push({ rowIndex, statusIndex });
      }
    });
  });

  if (matches.length === 0) return { outcome: "ignored", rows };
  if (matches.length !== 1) return { outcome: "fallback", rows };

  const [{ rowIndex, statusIndex }] = matches;
  const currentRow = rows[rowIndex];
  if (
    !canProjectBulkStatusTransition(
      currentRow.status[statusIndex]?.status,
      event.status
    )
  ) {
    return { outcome: "ignored", rows };
  }
  const nextRow = {
    ...currentRow,
    status: currentRow.status.map((status, index) =>
      index === statusIndex ? { ...status, status: event.status } : status
    ),
  };
  const nextRows = rows.map((row, index) =>
    index === rowIndex ? nextRow : row
  );
  return { outcome: "applied", row: nextRow, rows: nextRows };
};
